import React, { useEffect, useMemo } from 'react';
import {
  FunctionField,
  ReferenceField,
  Datagrid,
  TextField,
  useGetOne,
  useUnselectAll,
  linkToRecord,
  LinearProgress
} from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import StatusSelectField from '../../core/components/StatusSelectField';
import InvestorStatusSelectField from '../../core/components/InvestorStatusSelectField';
import { fundInvestorStatus } from '../fundinvestors/FundInvestorLookups';
import EditButtonWithOnClick from '../../core/components/EditButtonWithOnClick';
import { withStyles } from '@material-ui/core/styles';

const hideForNow = true; // as short term solution (it should be removed in the future)

const styles = () => ({
  table: {
    '& tbody>.MuiTableRow-root:hover': {
      background: 'unset',
      cursor: 'default'
    }
  },
  statusColumnStyle: {
    width: '68px'
  },
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block',
    paddingBottom: '10px'
  },
  expandLabel: {
    color: '#757575',
    fontSize: '14px',
    display: 'inline-block',
    transform: 'translateY(-10px)'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center'
  },
  editButton: {
    color: '#757575',
    minWidth: '24px',
    padding: '6px 0',
    '& span': {
      paddingRight: 0
    }
  },
  editButtonColumnStyle: {
    width: '24px'
  }
});

/**
 * Special field to return AKA through a getOne call.
 * ReferenceField unfortunately uses getMany which is disabled on investors
 */
const AKAField = ({ reference, id }) => {
  const { loaded, data } = useGetOne(reference, id);
  if (!loaded) {
    return <LinearProgress />;
  }
  if (data !== undefined) {
    return data.aka;
  }
  return null;
};

const InvestorFundDatagrid = props => {
  const {
    handleInvestorClick,
    handleInvestorFundEditButtonClick,
    classes,
    expand,
    permissions,
    hasShow,
    userProfile,
    hasEdit,
    hasList,
    ...dataGridProps
  } = props;

  //unselecting all the checkboxes from the deleting actions in the ComponenWillUnmount listener
  const unSelectAll = useUnselectAll(dataGridProps.resource);

  useEffect(() => () => unSelectAll(), [unSelectAll]);

  const hasReadInvestorPermission = useMemo(() => {
    return permissions.includes('AUTH_READ_INVESTOR_NA');
  }, [permissions]);

  if (props.total > 0) {
    return (
      <>
        {expand && <span className={classes.label}>Related Investors</span>}
        <Datagrid {...dataGridProps} className={classes.table}>
          <ReferenceField
            source="id"
            reference="fundinvestors"
            label="Investor Name"
            sortBy="investorName"
            link={
              hasReadInvestorPermission
                ? record => `${linkToRecord(`/investors`, record.investorId)}`
                : false
            }
          >
            <TextField source="investorName" id="investor name" />
          </ReferenceField>
          <InvestorStatusSelectField
            label="Investor Status"
            source="investorStatus"
            id="investor status"
            sortable={false}
            choices={fundInvestorStatus}
          />
          <FunctionField
            label="A.K.A."
            id="investor aka"
            render={record => (
              <AKAField reference="investors" id={record.investorId} />
            )}
          />
          <TextField
            source="investorId"
            label="Investor ID"
            sortable={false}
            id="investor id"
          />
          <StatusSelectField
            label="Relationship"
            source="status"
            id="investor relationship status"
            choices={fundInvestorStatus}
          />
          {handleInvestorClick || hideForNow ? (
            undefined
          ) : (
            <EditButtonWithOnClick
              label=""
              className={classes.editButton}
              headerClassName={classes.editButtonColumnStyle}
              onClick={handleInvestorFundEditButtonClick}
            />
          )}
        </Datagrid>
      </>
    );
  } else if (props.loaded) {
    return (
      <span className={expand ? classes.expandLabel : classes.label}>
        There are no related Investors to be displayed
      </span>
    );
  } else {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  }
};

export default withStyles(styles)(InvestorFundDatagrid);
